import { useState, Fragment, useMemo } from "react"
import PropTypes from "prop-types"
import { api } from "utils/api"
import AddAttendees from "components/admin/LegacyRegistration/AddAttendees"
import SectionFooter from "./SectionFooter"
import Transactions from "./Transactions"
import AttendeeSection from "./AttendeeSection"
import AttendeeDropdown from "./AttendeeDropdown"
import RegistrantContact from "./RegistrantContact"
import RegistrantQuestions from "./RegistrantQuestions"
import WaitlistedAttendees from "./WaitlistedAttendees"
import { StatusMessage } from "components/ui"
import { Button, StackView } from "@planningcenter/tapestry-react"
import { isPresent } from "utils/helpers"
import { colors } from "components/shared/admin_colors"
import useFlipperFlag from "hooks/use_flipper_flag"

const ATTENDEE_WAITLIST_STATUSES = ["notWaitlisted", "waitlisted"]
const byWaitlistStatus = value =>
  ATTENDEE_WAITLIST_STATUSES.reduce((result, status) => {
    result[status] = value
    return result
  }, {})

const DetailRegistration = ({
  event,
  registration: registrationFromProps,
  attendeeTypes,
  isActingStaff,
  isAdmin,
  churchCenterUrl
}) => {
  const [isValid, setIsValid] = useState(byWaitlistStatus(null))
  const [isEditing, setIsEditing] = useState(false)
  const [saveStatus, setSaveStatus] = useState({})
  const [registration, setRegistration] = useState(registrationFromProps)
  const [attendeesToUpdate, setAttendeesToUpdate] = useState(byWaitlistStatus([]))
  const [responsiblePerson, setResponsiblePerson] = useState(
    registrationFromProps.responsiblePerson
  )
  const [isRegistrantContactSaving, setIsRegistrantContactSaving] = useState(false)

  // isValid is an object with keys of ATTENDEE_WAITLIST_STATUSES
  // Each key is either true, false, or null indicating:
  // - there are changes to save and they are valid (true)
  // - there are changes to save, but they are invalid (false)
  // - there are no changes to save (null)
  const hasChanges = ATTENDEE_WAITLIST_STATUSES.some(
    waitlistStatus => isValid[waitlistStatus] !== null
  )
  const hasValidationErrors = ATTENDEE_WAITLIST_STATUSES.some(
    waitlistStatus => isValid[waitlistStatus] === false
  )

  const saveIsDisabled =
    saveStatus.isFetching || isRegistrantContactSaving || !hasChanges || hasValidationErrors
  const attendees = registration.attendees.reduce((result, attendee) => {
    const group = attendee.isWaitlisted ? "waitlisted" : "notWaitlisted"
    result[group] = result[group] || []
    result[group].push(attendee)
    return result
  }, {})

  const attendeeTypesWithWaitlist = event.attendeeTypes.filter(
    attendeeTypes => attendeeTypes.waitlist
  )
  const hasAttendeeTypeWithWaitlist = attendeeTypesWithWaitlist.length > 0

  const waitlistEnabled = useFlipperFlag("ROLLOUT_detailed_waitlist")
  const allowAttendeeCancel = waitlistEnabled
    ? registration.attendees.filter(attendee => !attendee.isCanceled).length > 1
    : registration.activeAttendeesCount > 1
  const allowAttendeeRestore = waitlistEnabled
    ? !registration.isCanceled
    : registration.activeAttendeesCount > 0

  const handleEditing = () => {
    setIsEditing(!isEditing)
    setSaveStatus({})
  }

  const handleAttendeesChange = waitlistStatus => {
    return (newAttendees, attrs) => {
      setAttendeesToUpdate(currentAttendeesToUpdate => ({
        ...currentAttendeesToUpdate,
        [waitlistStatus]: newAttendees
      }))
      setIsValid(currentIsValid => ({ ...currentIsValid, [waitlistStatus]: attrs.isValid }))
    }
  }

  const handleCancel = async () => {
    setIsValid(byWaitlistStatus(null))
    setAttendeesToUpdate(byWaitlistStatus([]))
    handleEditing()
  }

  const handleSave = () => {
    setSaveStatus({
      isFetching: true,
      text: "Saving...",
      icon: "spinner"
    })

    const peopleAttrs = ATTENDEE_WAITLIST_STATUSES.flatMap(waitlistStatus =>
      attendeesToUpdate[waitlistStatus].map(a => ({
        ...(a.attendeeId && { id: a.attendeeId }),
        ...(responsiblePerson && { responsible_person_id: responsiblePerson.id }),
        ...(a.prevAttendeeTypeId !== a.attendeeTypeId && {
          attendee_type_id: a.attendeeTypeId
        }),
        person_id: a.personId,
        waitlisted: waitlistStatus === "waitlisted"
      }))
    )

    api
      .put(registration.paths.show, {
        registration: { attendees_attributes: peopleAttrs }
      })
      .then(res => {
        setSaveStatus({
          isFetching: false,
          text: "Saved!",
          icon: "general#check",
          type: "success"
        })

        setRegistration(res.registration)
        setTimeout(() => handleEditing(), 500)
      })
      .catch(res => {
        setSaveStatus({
          isFetching: false,
          text: res.errors || "Changes not saved",
          icon: "general#x",
          type: "error"
        })
      })
  }

  const handleRegistrationContactChange = ({ isSaving, changes }) => {
    setIsRegistrantContactSaving(isSaving)
    if (changes) {
      setResponsiblePerson(changes.newResponsiblePerson)
      setRegistration(changes.newRegistration)
    }
  }

  const handleAttendeeCancel = ({ path, alertText }) => {
    return alertModal(
      {
        title: "Cancel Attendee?",
        text: alertText,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't cancel it",
        confirmButtonText: "Yes, cancel it!",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => {
        api.post(path).then(_ => {
          alertModal(
            {
              title: "Canceled!",
              type: "success",
              confirmButtonText: "Ok",
              closeOnConfirm: true
            },
            () => Turbolinks.visit()
          )
        })
      }
    )
  }

  const handleAttendeeRestore = ({ path, alertText }) => {
    return alertModal(
      {
        title: "Restore Attendee?",
        text: alertText,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't restore it",
        confirmButtonText: "Yes, restore it!",
        closeOnConfirm: true
      },
      () => {
        api.del(path).then(_ => {
          alertModal(
            {
              title: "Restored!",
              type: "success",
              confirmButtonText: "Ok",
              closeOnConfirm: true
            },
            () => Turbolinks.visit()
          )
        })
      }
    )
  }

  const suggestedPeople = useMemo(() => {
    const ids = new Set()
    const suggestions = []

    registration.attendees.map(a => {
      const aid = a?.accountCenterId || a?.account_center_id || a?.person?.accountCenterId
      if (aid && !ids.has(aid) && !a.canceledOn) {
        ids.add(aid)
        suggestions.push({
          ...a.person,
          personId: a.personId,
          account_center_id: aid,
          accountCenterId: aid,
          name: a.name
        })
      }
    })

    ATTENDEE_WAITLIST_STATUSES.forEach(waitlistStatus => {
      attendeesToUpdate[waitlistStatus].forEach(a => {
        const aid = a?.accountCenterId || a?.account_center_id || a?.person?.accountCenterId
        if (aid && !ids.has(aid)) {
          ids.add(aid)
          suggestions.push({
            personId: a.personId,
            account_center_id: aid,
            accountCenterId: aid,
            name: a.name
          })
        }
      })
    })

    if (responsiblePerson?.id && !ids.has(responsiblePerson?.account_center_id)) {
      suggestions.push(responsiblePerson)
    }

    return suggestions
  }, [responsiblePerson, attendeesToUpdate])

  return (
    <>
      <section className="pane mb-1 p-r print-box mb-2@print">
        <div className="p-r p-2 print-box mb-2@print">
          <div className="d-f ai-c jc-sb mb-2">
            <h1 className="h4 fw-600 mb-0">Attendees</h1>
            {isEditing ? (
              <StackView axis="horizontal" alignment="center" distribution="end">
                {isPresent(saveStatus) && <StatusMessage {...saveStatus} />}
                <Button
                  title="Cancel"
                  size="xs"
                  variant="outline"
                  disabled={saveStatus.isFetching}
                  onClick={handleCancel}
                  marginHorizontal={1}
                />
                <Button
                  title="Save"
                  size="xs"
                  disabled={saveIsDisabled}
                  theme="create"
                  onClick={handleSave}
                />
              </StackView>
            ) : (
              <>
                {!registration.isCanceled && (
                  <Button title="Edit" size="xs" variant="outline" onClick={handleEditing} />
                )}
              </>
            )}
          </div>

          {isEditing && (
            <AddAttendees
              accountsPeoplePath={event.paths.accountsPeople}
              attendeeTypes={attendeeTypes}
              onAttendeesChange={handleAttendeesChange("notWaitlisted")}
              existingAttendees={attendees.notWaitlisted || []}
            />
          )}

          {!isEditing &&
            attendees.notWaitlisted &&
            attendees.notWaitlisted.map(attendee => (
              <AttendeeDropdown
                key={attendee.id}
                attendee={attendee}
                attendeeTypeNotFree={
                  attendee.addonSelections.length > 0 || event.attendeeTypesNotFree
                }
              />
            ))}

          {!isEditing && (
            <dl className="d-f jc-sb d-f@print jc-sb@print">
              <dt>
                <strong>Total</strong>
              </dt>
              <dd>
                <strong>{registration.totalCostMinusDiscountsStr}</strong>
              </dd>
            </dl>
          )}
        </div>

        {isEditing && waitlistEnabled && hasAttendeeTypeWithWaitlist && (
          <div className="line p-r p-2" style={{ backgroundColor: colors.neutral_080 }}>
            <h1 className="h4 fw-600 mb-2">Waitlist</h1>
            <AddAttendees
              accountsPeoplePath={event.paths.accountsPeople}
              attendeeTypes={attendeeTypesWithWaitlist}
              onAttendeesChange={handleAttendeesChange("waitlisted")}
              existingAttendees={attendees.waitlisted || []}
              hideCost={true}
              displayWarnings={false}
            />
          </div>
        )}

        {!isEditing && waitlistEnabled && attendees.waitlisted && (
          <div className="line p-r p-2" style={{ backgroundColor: colors.neutral_080 }}>
            <WaitlistedAttendees
              attendees={attendees.waitlisted}
              registration={registration}
              event={event}
            />
          </div>
        )}
      </section>

      <SectionFooter
        event={event}
        registration={registration}
        isActingStaff={isActingStaff}
        hasAttendeeButton
        hasDiscountButton
        hasDeleteButton
        hasCancelButton
        hasFinalizeButton
        churchCenterUrl={churchCenterUrl}
      />

      {((!event.isFree && !registration.isForFreeEvent) ||
        registration.payments.length > 0 ||
        registration.refunds.length > 0) && <Transactions registration={registration} />}

      <section className="pane mb-1 p-r print-box mb-2@print">
        <div className="p-r p-2 print-box mb-2@print">
          <RegistrantContact
            registration={registration}
            suggestedPeople={suggestedPeople}
            onChange={handleRegistrationContactChange}
            isAdmin={isAdmin}
            isSaveDisabled={saveStatus.isFetching}
          />
        </div>
      </section>

      <SectionFooter
        event={event}
        registration={registration}
        isActingStaff={isActingStaff}
        hasEmailButton
        hideRegistrationDetail
      />

      {registration.questions.length > 0 && (
        <RegistrantQuestions registration={registration} questions={registration.questions} />
      )}

      <h4 className="d-n d-b@print">Attendees</h4>
      {attendees.notWaitlisted &&
        attendees.notWaitlisted.map(attendee => (
          <AttendeeSection
            key={attendee.id}
            attendee={attendee}
            event={event}
            onAttendeeCancel={handleAttendeeCancel}
            onAttendeeRestore={handleAttendeeRestore}
            isActingStaff={isActingStaff}
            isAdmin={isAdmin}
            allowCancel={allowAttendeeCancel}
            allowRestore={allowAttendeeRestore}
            registration={registration}
          />
        ))}

      {waitlistEnabled && attendees.waitlisted && (
        <>
          <h4 className="d-n d-b@print">Waitlisted Attendees</h4>
          {attendees.waitlisted.map(attendee => (
            <AttendeeSection
              key={attendee.id}
              attendee={attendee}
              event={event}
              onAttendeeCancel={handleAttendeeCancel}
              onAttendeeRestore={handleAttendeeRestore}
              isActingStaff={isActingStaff}
              isAdmin={isAdmin}
              allowCancel={allowAttendeeCancel}
              allowRestore={allowAttendeeRestore}
              registration={registration}
            />
          ))}
        </>
      )}
    </>
  )
}

DetailRegistration.propTypes = {
  churchCenterUrl: PropTypes.string,
  attendeeTypes: PropTypes.arrayOf(PropTypes.shape({})),
  event: PropTypes.shape({
    attendeeTypesNotFree: PropTypes.bool,
    isFree: PropTypes.bool,
    isSimple: PropTypes.bool,
    paths: PropTypes.shape({
      show: PropTypes.string,
      accountsPeople: PropTypes.string
    }),
    attendeeTypes: PropTypes.array
  }),
  registration: PropTypes.shape({
    id: PropTypes.number,
    totalPaymentsStr: PropTypes.string,
    totalRefundsStr: PropTypes.string,
    totalCostMinusDiscountsStr: PropTypes.string,
    activeAttendeesCount: PropTypes.number,
    totalDue: PropTypes.string,
    totalDueStr: PropTypes.string,
    refundDueStr: PropTypes.string,
    isForFreeEvent: PropTypes.bool,
    isCanceled: PropTypes.bool,
    isRefundDue: PropTypes.bool,
    canceledOn: PropTypes.string,
    isBalanceDue: PropTypes.bool,
    isFinalized: PropTypes.bool,
    restoreAlertText: PropTypes.string,
    isFormDue: PropTypes.bool,
    responsiblePerson: PropTypes.shape({
      account_center_id: PropTypes.number
    }),
    attendees: PropTypes.array,
    payments: PropTypes.array,
    refunds: PropTypes.array,
    questions: PropTypes.array,
    paths: PropTypes.shape({
      showResponsiblePerson: PropTypes.string,
      edit: PropTypes.string,
      cancel: PropTypes.string,
      newDiscount: PropTypes.string,
      show: PropTypes.string,
      newEmail: PropTypes.string,
      confirmEmail: PropTypes.string,
      balanceDueEmail: PropTypes.string,
      formDueEmail: PropTypes.string,
      finalize: PropTypes.string,
      newPayment: PropTypes.string,
      newRefund: PropTypes.string
    })
  }),
  isActingStaff: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onResendEmail: PropTypes.func,
  onFinalize: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
  onRestore: PropTypes.func
}

export default DetailRegistration
